import classNames from "classnames";
import { capitalize } from "lodash";
import { useCallback } from "react";
import { CollapsibleState } from "~/components/build/Show/pages/steps/list/components/TreeItem/CollapsibleState";
import {
  TreeNode,
  TreeItemContext,
  createStepNode,
} from "~/components/build/Show/pages/steps/list/components/TreeNode";
import { STATE_HEADER_HEIGHT } from "~/components/build/Show/pages/steps/list/components/constants";
import { useBuild } from "~/components/build/Show/lib/BuildContext";
import { Build } from "~/components/build/Show/lib/types";
import { useBuildPreferencesStore } from "~/components/build/Show/lib/useBuildPreferencesStore";
import { useBuildStore } from "~/components/build/Show/lib/useBuildStore";
import { useStepsForStateQuery } from "~/components/build/Show/lib/useSteps";
import Icon from "~/components/shared/Icon";
import { FilterByOption } from "~/components/build/Show/lib/filterConstants";
import { FilterStateIcon } from "~/components/build/Show/lib/FilterStateIcon";
import { StepRow } from "~/components/build/Show/pages/steps/list/components/StepRow";
import { Badge } from "~/components/build/Show/pages/steps/list/components/Badge";

interface Props extends TreeItemContext {
  state: FilterByOption;
  count: number;
}

export function StateHeaderItem({ state, count, ...props }: Props) {
  const { build } = useBuild();
  const stepsGroupedByState = useBuildStore((state) => state.stepsGroupedByState);
  const setExpandedStates = useBuildPreferencesStore((state) => state.setExpandedStates);
  const expandedStates = useBuildPreferencesStore((state) => state.expandedStates || []);
  const query = useStepsForStateQuery(build, state, {
    enabled:
      props.collapsibleState === CollapsibleState.Expanded ||
      props.collapsibleState === CollapsibleState.Full,
  });

  const toggleState = useCallback(() => {
    expandedStates.find((expandedState) => expandedState === state)
      ? setExpandedStates(expandedStates.filter((expandedState) => expandedState !== state))
      : setExpandedStates([...expandedStates, state]);
  }, [expandedStates, setExpandedStates, state]);

  return (
    <div className="relative w-full">
      <button
        onClick={toggleState}
        data-testid={`${state}-group`}
        style={{ height: STATE_HEADER_HEIGHT }}
        className={classNames(
          "flex w-full cursor-pointer items-center self-start rounded-md bg-slate-100 py-1 pl-1.5 hover:bg-gray-100 dark:hover:bg-gray-100",
          {
            "animate-none": query.isLoading && !stepsGroupedByState.get(state)?.length,
          },
        )}
      >
        <StepRow
          icon={<FilterStateIcon filter={state} />}
          label={
            <div className="inline-flex items-center gap-x-2">
              <span className="text-charcoal-600 text-sm font-medium">{capitalize(state)}</span>
              <Badge className="tabular-nums" data-testid={`${state}-group-count`}>
                {count} {count === 1 ? "step" : "steps"}
              </Badge>
            </div>
          }
        />
      </button>

      <div
        aria-hidden="true"
        className="pointer-events-none absolute left-[0.375rem] top-1/2 flex h-6 w-6 -translate-y-1/2 select-none items-center justify-center"
      >
        <Icon
          icon="heroicons/outline/chevron-right"
          className="h-3 w-3 transition-transform duration-150 group-aria-expanded:rotate-90"
        />
      </div>
    </div>
  );
}

export const createStateHeaderNode = (
  state: FilterByOption,
  count: number,
  build: Build,
): TreeNode => {
  return {
    id: state,
    height: STATE_HEADER_HEIGHT,
    sticky: true,
    render: (props) => <StateHeaderItem key={state} state={state} count={count} {...props} />,
    childNodes: (collapsibleState: CollapsibleState) => {
      if (collapsibleState === CollapsibleState.Expanded) {
        const steps = useBuildStore.getState().stepsGroupedByState.get(state) || [];

        return steps.map((step) => createStepNode(step, build));
      }
      return [];
    },
  };
};
